import {Injectable, inject} from '@angular/core';
import {Platform} from '@angular/cdk/platform';
import {LoggerService} from './logger.service';
import {Settings} from '../settings/settings';

type PickerKind = 'camera' | 'gallery';

/** Only used for logging: how long a picker may take before we note that we did not see it open */
const PICKER_DETECTION_MS = 2000;

@Injectable({
    providedIn: 'root'
})
export class CameraPermissionService {
    private platform = inject(Platform);
    private logger = inject(LoggerService);

    /**
     * Opens the camera or photo picker of the device, during the tap itself: browsers ignore a click() that runs
     * later, e.g. after awaiting a check (WebKit on iOS always, Chrome on Android after a permission prompt).
     * Nothing is checked beforehand. The pickers do not use the camera permission of the website (that one is for
     * getUserMedia), and the earlier checks (a getUserMedia probe and a 700 ms timer with a "Geen toegang" message)
     * only gave false alarms in production, and could even keep the camera busy.
     */
    openInput(input: HTMLInputElement, kind: PickerKind): void {
        if (!this.platform.IOS && !Settings.isIpad) {
            // On iOS the page stays visible under the picker, so there is nothing to detect there
            this.logIfPickerNotSeen(input, kind);
        }
        input.click();
    }

    /** Logs (without bothering the user) when the page shows no sign of a picker taking over, to spot real failures */
    private logIfPickerNotSeen(input: HTMLInputElement, kind: PickerKind) {
        const cleanup = () => {
            clearTimeout(timer);
            window.removeEventListener('blur', cleanup);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            input.removeEventListener('change', cleanup);
            input.removeEventListener('cancel', cleanup);
        };
        const onVisibilityChange = () => {
            if (document.hidden) {
                cleanup();
            }
        };
        window.addEventListener('blur', cleanup);
        document.addEventListener('visibilitychange', onVisibilityChange);
        input.addEventListener('change', cleanup);
        input.addEventListener('cancel', cleanup);
        const timer = setTimeout(() => {
            cleanup();
            this.logger.logEvent('CAMERA_FLOW', 'picker_not_detected', {
                kind, timeoutMs: PICKER_DETECTION_MS, visibility: document.visibilityState
            });
        }, PICKER_DETECTION_MS);
    }
}
